export const VIEWPORT_LATEST_WINS_REQUEST_MARKER = 'KAIROS_VIEWPORT_LATEST_WINS_REQUEST';
export const VIEWPORT_DIRECTION_REVERSAL_CANCEL_MARKER = 'KAIROS_VIEWPORT_DIRECTION_REVERSAL_CANCEL';

export const isExpectedViewportImageSource = (expectedSource, elementSource, baseUrl) => {
  if (!expectedSource) return true;
  if (!elementSource) return false;
  try {
    return new URL(expectedSource, baseUrl).href === new URL(elementSource, baseUrl).href;
  } catch (_) {
    return false;
  }
};

const normalizedDirection = (value, fallback = 1) => {
  const numeric = Number(value);
  if (numeric > 0) return 1;
  if (numeric < 0) return -1;
  return fallback >= 0 ? 1 : -1;
};

export const createViewportInteractiveRequestCoordinator = ({ onAbortVisibleImage, initialVisibleSlice = null } = {}) => {
  let visibleGeneration = 0;
  const normalizeOptionalSlice = value => (value !== null && value !== undefined && Number.isFinite(Number(value)) ? Number(value) : null);
  let latestSlice = normalizeOptionalSlice(initialVisibleSlice);
  let acceptedVisibleSlice = latestSlice;
  let acceptedVisibleGeneration = 0;
  let visiblePending = false;
  let direction = 1;
  let abortedVisible = 0;
  let visibleImage = null;

  const abortVisibleImage = () => {
    const entry = visibleImage;
    visibleImage = null;
    const image = entry?.image;
    if (!image) return false;
    image.onload = null;
    image.onerror = null;
    try { image.src = ''; } catch (_) {}
    try { onAbortVisibleImage?.(image); } catch (_) {}
    try { entry.onAbort?.(); } catch (_) {}
    abortedVisible += 1;
    return true;
  };

  const beginVisible = ({ slice, direction: requestedDirection } = {}) => {
    // A visible diagnostic frame always outranks the prior visible image. Page-
    // global speculative work is preempted separately by cineResourceGovernor.
    abortVisibleImage();
    visibleGeneration += 1;
    latestSlice = Number.isFinite(Number(slice)) ? Number(slice) : 0;
    visiblePending = true;
    direction = normalizedDirection(requestedDirection, direction);
    return { generation: visibleGeneration, slice: latestSlice, direction };
  };

  const acceptVisible = ({ generation = visibleGeneration, slice = latestSlice } = {}) => {
    if (generation !== visibleGeneration || acceptedVisibleGeneration === generation) return false;
    acceptedVisibleGeneration = generation;
    acceptedVisibleSlice = Number.isFinite(Number(slice)) ? Number(slice) : latestSlice;
    visiblePending = false;
    return true;
  };

  const failVisible = ({ generation = visibleGeneration } = {}) => {
    if (generation !== visibleGeneration || acceptedVisibleGeneration === generation || !visiblePending) return false;
    latestSlice = acceptedVisibleSlice;
    visiblePending = false;
    return true;
  };

  const registerVisibleImage = ({ image, generation = visibleGeneration, onAbort = null } = {}) => {
    if (!image || generation !== visibleGeneration) return false;
    abortVisibleImage();
    visibleImage = { image, generation, onAbort };
    return true;
  };

  const completeVisibleImage = ({ image, generation = visibleGeneration } = {}) => {
    if (!visibleImage || visibleImage.image !== image || visibleImage.generation !== generation) return false;
    visibleImage = null;
    image.onload = null;
    image.onerror = null;
    return true;
  };

  const reset = ({ initialVisibleSlice: nextInitialVisibleSlice = null } = {}) => {
    abortVisibleImage();
    visibleGeneration += 1;
    latestSlice = normalizeOptionalSlice(nextInitialVisibleSlice);
    acceptedVisibleSlice = latestSlice;
    acceptedVisibleGeneration = visibleGeneration;
    visiblePending = false;
    direction = 1;
    return visibleGeneration;
  };

  return {
    beginVisible,
    isCurrentVisible: generation => generation === visibleGeneration,
    acceptVisible,
    failVisible,
    registerVisibleImage,
    completeVisibleImage,
    abortVisibleImage,
    reset,
    snapshot: () => ({
      visibleGeneration,
      latestSlice,
      acceptedVisibleSlice,
      acceptedVisibleGeneration,
      visiblePending,
      direction,
      visibleImageActive: Boolean(visibleImage),
      abortedVisible,
    }),
  };
};
